import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { PrFormConstructor } from '@app/components/pr-form/pr-form.contructor';
import { PrModalConfirmComponent } from '@app/components/pr-modal/pr-modal-confirm/pr-modal-confirm.component';
import { AttributionDao } from '@app/dao/attribution/attribution.dao';
import { PledgeRead } from '@app/forms/subscription/definition/interface/Pledge';
import { FormControlOption } from '@app/forms/subscription/definition/interface/formControlOption.interface';
import { HttpApiService } from '@app/providers/http-api/http-api.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import * as _ from 'lodash';
import moment from 'moment';
import { Observable, forkJoin, from, of, catchError, delay, finalize, mergeMap, take, tap } from 'rxjs';

import {
  API_ROUTE_SEND_ELECTRONIC_SIGNATURE,
  API_ROUTE_SEND_PAPER_SIGNATURE,
  API_ROUTE_SUBSCRIPTION_FILE,
} from '../../api/api-router';
import { CommandEnum } from '../../definition/interface/enum';
import { SubscriptionFacadeService } from '../../services/subscription-facade.service';
import { VerifyIdentityDocumentService } from '../../services/verifyIdentityDocument.service';
import { EaSubscriptionBuilder } from './ea-subscription-form.builder';

interface DocumentsSelect {
  check: string[];
  gedId: string;
  prGedId: number;
  index: number;
  name: string;
  signatures: any[];
  universignCheckBoxes?: string[];
}

@UntilDestroy()
@Component({
    selector: 'send-documents',
    templateUrl: './send-documents.component.html',
    styleUrls: ['./send-documents.component.scss'],
    standalone: false
})
export class SendDocumentsComponent
  extends EaSubscriptionBuilder
  implements OnInit, AfterViewInit
{
  @ViewChild(PrModalConfirmComponent, { static: true })
  modal: PrModalConfirmComponent;

  optionsLabel: FormControlOption[] = [
    {
      label: '',
      value: 'print',
    },
    {
      label: '',
      value: 'doc',
    },
    {
      label: '',
      value: 'empty',
    },
  ];
  loading: boolean = false;
  pledge: PledgeRead;
  allDocumentsNotHaveSignatures: boolean = false;

  verificationResults: { display: string; icon: string; text: string } = {
    display: '',
    icon: '',
    text: '',
  };
  certif: boolean = false;
  isCristalisOrganization: boolean = false;
  cristalisOrganizationId: number = 251251251;
  dataIsMissing: boolean = false;
  modalMessage =
    'La souscription passera en statut en attente de signature <br /> et ne sera plus éditable.';
  signatureType: 'ELECTRONIC' | 'PAPER' = 'ELECTRONIC';

  @Output() onConfirm = new EventEmitter<any>();

  constructor(
    private subscriptionFacade: SubscriptionFacadeService,
    protected formConstructor: PrFormConstructor,
    private cdr: ChangeDetectorRef,
    private httpApiService: HttpApiService,
    private verificationService: VerifyIdentityDocumentService,
    private attributionDao: AttributionDao,
  ) {
    super(formConstructor);
  }
  ngAfterViewInit(): void {}

  ngOnInit(): void {
    this.initializeForm();
    this.attributionDao.getOrganization().subscribe((data: any) => {
      this.isCristalisOrganization = this.cristalisOrganizationId === data.id;
    });

    this.pledge = this.subscriptionFacade.getPledge();
    this.signatureType = this.pledge?.signatureType || 'ELECTRONIC';

    // Séquençage V2 — branche V2 : pas de récap des documents (déjà vus aux étapes
    // précédentes PackBuilder + document-compliance). On garde uniquement le verif identité
    // legacy + Cristalis check + choix du type de signature.
    if (this.pledge?.legacyFlow === false) {
      this.checkMissingData(this.pledge);
      this.certif = this.verificationService.calculateCertif(this.pledge);
      if (this.certif) {
        this.verifyIdentityDocuments();
      }
      return;
    }

    this.checkMissingData(this.pledge);
    const documents = this.pledge?.documents;
    this.certif = this.verificationService.calculateCertif(this.pledge);
    if (this.certif) {
      this.verifyIdentityDocuments();
    }
    this.checkDocumentSignatures(documents);
  }

  get isV2(): boolean {
    return this.pledge?.legacyFlow === false;
  }
  private initializeForm(): void {
    this.subscriptionFacade.initForm(this);
  }
  private verifyIdentityDocuments(): void {
    this.verificationResults.display = !!this.pledge.parameters
      .hasUniversignPrevalidationValidated
      ? 'success'
      : 'warning';
    this.verificationResults.icon = !!this.pledge.parameters
      .hasUniversignPrevalidationValidated
      ? 'success'
      : 'warning';
    this.verificationResults.text = !!this.pledge.parameters
      .hasUniversignPrevalidationValidated
      ? "La prévalidation automatique des documents d'identité est validée, la signature électronique est disponible."
      : this.pledge.parameters.universignPrevalidationErrors?.length
        ? this.pledge.parameters.universignPrevalidationErrors.join('\n')
        : "La prévalidation automatique des documents d'identité n'est pas validée, la signature électronique nécessitera une validation manuelle des documents d'identité par universign.";
  }

  private checkMissingData(pledge: PledgeRead) {
    const owner = pledge.owners?.[0];
    const product = pledge.products?.[0]?.parameters;
    const company = owner?.company;

    this.dataIsMissing =
      !owner ||
      owner.userBirthDate === null ||
      !owner ||
      owner.userBirthCity === null ||
      !owner ||
      owner.userBirthCountry === null ||
      !owner ||
      owner.userBirthZipCode === null ||
      !owner ||
      owner.userSocialSecurityNumber === null ||
      !owner ||
      owner.addressStreet === null ||
      !owner ||
      owner.addressCity === null ||
      !owner ||
      owner.addressZipCode === null ||
      !owner ||
      owner.contactPhones.length === 0 ||
      !owner ||
      owner.userEmail === null ||
      !owner ||
      owner.userMaritalSituation === null ||
      !owner ||
      owner.userNifCode === null ||
      !company ||
      company.addressSiegeSocial === null ||
      !company ||
      company.name === null ||
      !product ||
      !product.enrollmentOffice ||
      product.enrollmentOffice === null ||
      !product ||
      !product.directDebitIban ||
      product.directDebitIban.holder === null ||
      !product ||
      !product.directDebitIban ||
      product.directDebitIban.bankName === null ||
      !product ||
      !product.directDebitIban ||
      product.directDebitIban.bic === null ||
      !product ||
      !product.realEstateAddress;
  }

  confirmeMissingData() {
    this.dataIsMissing = false;
    this.onSubmit();
  }

  handleClick2(verificationResult: any) {
    if (verificationResult.dangerTextClick) {
      verificationResult.dangerTextClick(); // Execute the click event function
    }
  }
  formatText(text: string): string {
    const dangerTextWithClick = text.replace(
      /Cliquez ici pour changer de document d'identité/,
      `<a class="ici-link">Cliquez ici</a> pour changer de document d'identité`,
    );
    return dangerTextWithClick;
  }

  handleClick(event: Event, verificationResult: any) {
    const target = event.target as HTMLElement;
    if (target.tagName === 'A') {
      if (verificationResult.dangerTextClick) {
        verificationResult.dangerTextClick();
      }
    }
  }
  setQuestionStep() {
    const q = this.subscriptionFacade
      ?.getQuestions()
      ?.findIndex(q => q.name === 'document');
    if ((q || q == 0) && q !== -1) {
      this.subscriptionFacade.setStep(Number(q) + 1);
    }
  }
  private checkDocumentSignatures(documents: any[]): void {
    this.allDocumentsNotHaveSignatures = documents.every(
      doc => !doc?.signatures?.length,
    );
  }
  getObjectKeys(obj: any): string[] {
    return Object.keys(obj);
  }
  hasDanger(verificationResults) {
    return Object.values(verificationResults).some(
      (result: any) => result.display === 'danger',
    );
  }
  onSubmit() {
    if (this.signatureType === 'ELECTRONIC') {
      if (this.isCristalisOrganization && this.dataIsMissing) {
        this.modalMessage =
          "<span class='text-danger'>Des champs importants sont encore vides. <br /> Êtes-vous sûr de vouloir envoyer les documents ?</span> <br /><br/> En cas de confirmation: La souscription passera en statut en attente de signature <br /> et ne sera plus éditable.";
      }
      this.modal.open();
    } else {
      this.onConfirmPaperSubmit();
    }
  }

  /**
   * Séquençage V2 — Envoi V2 électronique : POST /sendElectronicSignature
   * avec body vide. le hook backend court-circuite la route legacy via
   * PledgeSendElectronicSignatureV2Service qui envoie le premier pack DRAFT à Universign.
   */
  private onConfirmSubmitV2(): void {
    this.loading = true;
    const route = API_ROUTE_SEND_ELECTRONIC_SIGNATURE.replace(
      ':pledgeId',
      this.entity.id,
    );
    this.httpApiService
      .post(route, [])
      .pipe(
        take(1),
        untilDestroyed(this),
        catchError(err => {
          this.loading = false;
          console.error(err);
          this.notifyApiError(
            err,
            "Erreur lors de l'envoi en signature électronique",
          );
          throw err;
        }),
        tap((data: PledgeRead) => {
          this.subscriptionFacade.emitPledge(data);
          this.subscriptionFacade.setCommand([CommandEnum.DOCUMENTS_SENT]);
          this.subscriptionFacade.nextStep();
        }),
        finalize(() => {
          this.loading = false;
        }),
      )
      .subscribe();
  }

  /**
   * Séquençage V2 — Envoi V2 papier : POST /send-paper-signature
   * direct. le hook backend court-circuite via PledgeSendPaperSignatureV2Service
   * qui signe tous les packs DRAFT en une passe (pas de séquençage en mode papier — parité
   * legacy). Pas de téléchargement préalable de PDFs en V2 — à voir si nécessaire (gap dédié).
   */
  private onConfirmPaperSubmitV2(): void {
    this.loading = true;
    const route = API_ROUTE_SEND_PAPER_SIGNATURE.replace(
      ':pledgeId',
      this.entity.id,
    );
    this.httpApiService
      .post(route)
      .pipe(
        take(1),
        untilDestroyed(this),
        catchError(err => {
          this.loading = false;
          console.error(err);
          this.notifyApiError(
            err,
            "Erreur lors de l'envoi en signature papier",
          );
          throw err;
        }),
        tap((data: PledgeRead) => {
          this.subscriptionFacade.emitPledge(data);
          this.subscriptionFacade.nextStep();
        }),
        finalize(() => {
          this.loading = false;
        }),
      )
      .subscribe();
  }

  /**
   * Séquençage V2 — Affiche un toast d'erreur pour les exceptions remontées par
   * l'API. Reprend le format legacy (cf. autres catchError du composant) :
   * "Erreur ... : message backend" ou fallback générique si pas de message structuré.
   */
  private notifyApiError(err: any, fallback: string): void {
    const { message, developperMessage } = err?.error || {};
    const text = message
      ? message + (developperMessage ? ': ' + developperMessage : '')
      : fallback;
    this.notificationService.error(text);
  }

  onConfirmPaperSubmit() {
    // Séquençage V2 — branche V2 délègue à onConfirmPaperSubmitV2 (pas de
    // téléchargement préalable, hook backend gère tout).
    if (this.isV2) {
      this.onConfirmPaperSubmitV2();
      return;
    }
    this.form.markAsDirty();
    this.loading = true;

    const documents = this.pledge?.documents;
    const gedIds = documents
      .filter(doc => !!doc.prGedId)
      .map(doc => doc.prGedId);

    let userName =
      this.pledge?.owners?.[0]?.userFirstName +
      '-' +
      this.pledge?.owners?.[0]?.userLastName;

    const downloadObservables = gedIds.map(gedId => {
      let docName = documents.filter(doc => doc.prGedId == gedId)[0]?.name;
      if (!docName) docName = String(gedId);
      let date = moment().format('DD-MM-YY');
      let name = (userName + ' ' + date + ' ' + docName)
        .replace(/ /g, '-')
        .replace('Bulletin-de-souscription', 'BS');
      return this.downloadDocument(gedId, name);
    });

    forkJoin(downloadObservables)
      .pipe(
        untilDestroyed(this),
        catchError(err => {
          this.loading = false;
          console.error(err);
          this.notificationService.error(
            'Erreur lors du téléchargement des documents',
          );
          throw err;
        }),
      )
      .subscribe(() => {
        const route = API_ROUTE_SEND_PAPER_SIGNATURE.replace(
          ':pledgeId',
          this.entity.id,
        );
        this.httpApiService
          .post(route)
          .pipe(
            take(1),
            catchError(err => {
              console.error(err);
              const { message, developperMessage } = err?.error || {};
              this.notificationService.error(
                message + ': ' + developperMessage,
              );
              throw err;
            }),
            tap((data: PledgeRead) => {
              this.subscriptionFacade.emitPledge(data);
              this.subscriptionFacade.nextStep();
            }),
            finalize(() => {
              this.loading = false;
            }),
          )
          .subscribe();
      });
  }

  onConfirmSubmit() {
    // Séquençage V2 — branche V2 délègue à onConfirmSubmitV2 (envoi
    // simplifié sans filtrage per-doc, hook backend gère tout).
    if (this.isV2) {
      this.onConfirmSubmitV2();
      return;
    }
    this.form.markAsDirty();
    const docs: DocumentsSelect[] = this.form.value?.documentsForm;
    this.process(docs).subscribe(() => {
      this.subscriptionFacade.setCommand([CommandEnum.DOCUMENTS_SENT]);
      this.subscriptionFacade.submitPledge(this);
    });
  }

  validate(docs: DocumentsSelect[]): boolean {
    return !docs.some(doc => !doc.check.length);
  }
  process(docs: DocumentsSelect[]): Observable<any> {
    const printsFile = this.findPrint(docs);
    const docsDownLoads = this.findGedIdsDoc(docs);

    let nested: Observable<any>[] = [];

    if (printsFile?.length && !this.hasDanger(this.verificationResults)) {
      nested.push(this.digitalLoan(printsFile));
    }
    if (docsDownLoads?.length) {
      nested.push(this.downloadDocumentsMerge(docs));
    }

    if (!nested?.length) {
      nested.push(of(true));
    }

    return this.executeNestedOperations(nested);
  }
  private executeNestedOperations(nested: Observable<any>[]): Observable<any> {
    this.loading = true;

    return forkJoin(nested).pipe(
      untilDestroyed(this),
      tap(() => {
        this.loading = true;
      }),
      catchError(err => {
        this.loading = false;
        console.error(err);
        return err;
      }),
      finalize(() => {
        this.loading = false;
      }),
    );
  }
  findPrint(docs: DocumentsSelect[]) {
    return this.omit(
      docs?.filter(doc => doc?.check?.includes('print')),
      ['check', 'index'],
    );
  }
  findGedIdsDoc(docs: DocumentsSelect[]) {
    const docsDownLoaded = docs?.filter(doc => doc?.check?.includes('doc'));
    return docsDownLoaded?.map(item => item?.prGedId);
  }

  omit(docs: DocumentsSelect[], prop: string[]) {
    return _.map(docs, doc => _.omit(doc, prop));
  }

  private downloadDocumentsMerge(docs) {
    const gedIds = this.findGedIdsDoc(docs);

    let userName =
      this.pledge?.owners?.[0]?.userFirstName +
      '-' +
      this.pledge?.owners?.[0]?.userLastName;

    return from(gedIds).pipe(
      mergeMap(gedId => {
        let docName = docs.filter(doc => doc.prGedId == gedId)[0]?.name;
        if (!docName) docName = gedId;
        let date = moment().format('DD-MM-YY');
        let name = (userName + ' ' + date + ' ' + docName)
          .replace(/ /g, '-')
          .replace('Bulletin-de-souscription', 'BS');
        return this.downloadDocument(gedId, name);
      }),
    );
  }

  digitalLoan(body) {
    const route = API_ROUTE_SEND_ELECTRONIC_SIGNATURE.replace(
      ':pledgeId',
      this.entity.id,
    );
    return this.httpApiService.post(route, body).pipe(
      take(1),
      catchError(err => {
        console.error(err);
        const { message, developperMessage } = err?.error || {};
        this.notificationService.error(message + ': ' + developperMessage);
        throw err;
      }),
      tap((data: PledgeRead) => {
        data && this.subscriptionFacade.emitPledge(data);
      }),
    );
  }
  downloadDocument(docId, name) {
    const route = API_ROUTE_SUBSCRIPTION_FILE.replace(
      ':accountId',
      this.entity.accountId,
    ).replace(':documentId', docId);
    return this.httpApiService
      .getWithOptions(route, {
        responseType: 'arraybuffer',
      })
      .pipe(
        take(1),
        delay(1000),
        tap(resp => {
          this.createLink(resp, name);
        }),
        catchError(err => {
          console.error(err);
          return err;
        }),
        finalize(() => {}),
      );
  }
  createLink(resp, name) {
    let blob = new Blob([resp], { type: 'application/pdf' });
    let link = document.createElement('a');
    document.body.appendChild(link);
    link.href = window.URL.createObjectURL(blob);
    link.download = `${name}.pdf`;
    link.click();
    document.body.removeChild(link);
    this.cdr.detectChanges();
  }

  prev() {
    this.subscriptionFacade.previousStep();
  }

  onSignatureTypeChange(type: 'ELECTRONIC' | 'PAPER') {
    this.signatureType = type;
    if (this.pledge) {
      this.pledge.signatureType = type;
    }
  }
}
